import {option, Option} from "ts-option";
import {ColumnDefinition, ColumnTypeEnum} from "./ColumnDefinition";
import {DateFilterType, FilterModelType} from "../../components/v5/filters/dateFilter/DateFilter";
import {BetweenFilterModel} from "../../components/v5/filters/dateFilter/BetweenFilter";
import {WithinTheLastFilterModel} from "../../components/v5/filters/dateFilter/WithinTheLastFilter";
import {InRangeFilterModel} from "../../components/v5/filters/dateFilter/InRangeFilter";
import DateFilterModel from "./DateFilterModel";
import {
	CreateWorkspaceFilterApiDto,
	DateFilterValueItemApiDto,
	FilterValueApiDto,
	QuerySearchFilterApiDto,
	WorkspaceViewFilterApiDto
} from "../../api/model/rest";
import CollectionFilterModel, {CollectionFilterType} from "./CollectionFilterModel";
import {NumberFilterModelType, NumberFilterType} from "../../components/v5/filters/numberFilter/NumberFilter";
import NumberFilterModel from "./NumberFilterModel";
import {NumberCompareFilterModel} from "../../components/v5/filters/numberFilter/NumberCompareFilter";
import {NumberBetweenFilterModel} from "../../components/v5/filters/numberFilter/NumberBetweenFilter";
import {MoreThanAgoFilterModel} from "../../components/v5/filters/dateFilter/MoreThanAgoFilter";
import {UpToFilterModel} from "../../components/v5/filters/dateFilter/UpToFilter";
import {MoreThanFromNowFilterModel} from "../../components/v5/filters/dateFilter/MoreThanFromNowFilter";
import * as _ from 'underscore';
import DateUtils from "../../utils/DateUtils";

export default class Filter {
	constructor(public readonly id: string,
		public readonly columnDefinition: ColumnDefinition,
		public readonly type: string,
		public readonly values: Option<FilterValueType>,
	) {}

	copy({id = this.id, type = this.type, values = this.values}): Filter {
		return new Filter(id, this.columnDefinition, type, values);
	}

	static fromJson(json: WorkspaceViewFilterApiDto, columnDefinition: ColumnDefinition) {
		const operatorType = json.type;
		let value = option([...(json.values || [])]) as Option<FilterValueType>;

		if (columnDefinition.type == ColumnTypeEnum.Date || columnDefinition.type == ColumnTypeEnum.Appointments) {
			if (operatorType == DateFilterType.Between) {
				value = option(BetweenFilterModel.fromJson(json.values as string[]))
			}
			else if (operatorType == DateFilterType.MoreThanAgo) {
				value = option(MoreThanAgoFilterModel.fromJson(json.values as DateFilterValueItemApiDto[]))
			}
			else if (operatorType == DateFilterType.WithinTheLast) {
				value = option(WithinTheLastFilterModel.fromJson(json.values as DateFilterValueItemApiDto[]))
			}
			else if (operatorType == DateFilterType.InRange) {
				value = option(InRangeFilterModel.fromJson(json.values as DateFilterValueItemApiDto[]))
			}
			else if (operatorType == DateFilterType.UpTo) {
				value = option(UpToFilterModel.fromJson(json.values as DateFilterValueItemApiDto[]))
			}
			else if (operatorType == DateFilterType.MoreThanFromNow) {
				value = option(MoreThanFromNowFilterModel.fromJson(json.values as DateFilterValueItemApiDto[]))
			}
			else if (columnDefinition.type == ColumnTypeEnum.Appointments) {
				value = option(new CollectionFilterModel(operatorType as CollectionFilterType));
			}
			else {
				value = option(new DateFilterModel(operatorType as DateFilterType));
			}
		}
		else if (columnDefinition.type == ColumnTypeEnum.JobCustomField ||
			columnDefinition.type == ColumnTypeEnum.LeadCustomField) {
			const id = json.values.shift();
			if (operatorType === DateFilterType.Between) {
				value = option([id, BetweenFilterModel.fromJson(json.values as string[])]);
			}
			else if (operatorType === DateFilterType.InRange) {
				value = option([id, InRangeFilterModel.fromJson(json.values as DateFilterValueItemApiDto[])]);
			}
			else if (operatorType === DateFilterType.UpTo) {
				value = option([id, UpToFilterModel.fromJson(json.values as DateFilterValueItemApiDto[])]);
			}
			else if (operatorType === DateFilterType.MoreThanFromNow) {
				value = option([id, MoreThanFromNowFilterModel.fromJson(json.values as DateFilterValueItemApiDto[])]);
			}
			else if (operatorType === DateFilterType.WithinTheLast) {
				value = option([id, WithinTheLastFilterModel.fromJson(json.values as DateFilterValueItemApiDto[])]);
			}
			else if (operatorType === DateFilterType.MoreThanAgo) {
				value = option([id, MoreThanAgoFilterModel.fromJson(json.values as DateFilterValueItemApiDto[])]);
			}
			else if (operatorType === DateFilterType.Empty || operatorType === DateFilterType.All) {
				value = option([id, new DateFilterModel(operatorType as DateFilterType)]);
			}
		}
		else if (columnDefinition.type === ColumnTypeEnum.Currency || columnDefinition.type === ColumnTypeEnum.Decimal ||
			columnDefinition.type == ColumnTypeEnum.Long || columnDefinition.type == ColumnTypeEnum.Percentage) {
			if (operatorType === NumberFilterType.Equal) {
				value = option(NumberCompareFilterModel.fromJson(json.values as number[], NumberFilterType.Equal));
			}
			else if (operatorType === NumberFilterType.GreaterThan) {
				value = option(NumberCompareFilterModel.fromJson(json.values as number[], NumberFilterType.GreaterThan));
			}
			else if (operatorType === NumberFilterType.LessThan) {
				value = option(NumberCompareFilterModel.fromJson(json.values as number[], NumberFilterType.LessThan));
			}
			else if (operatorType === NumberFilterType.Between) {
				value = option(NumberBetweenFilterModel.fromJson(json.values as number[]));
			}
		}
		else if (columnDefinition.type == ColumnTypeEnum.String) {
			value = value.flatMap((v) => option((v as string[])[0]))
		}
		return new Filter(json.id, columnDefinition, operatorType, value)
	}

	toCreateJson(): CreateWorkspaceFilterApiDto {
		return {columnDefinitionId: this.columnDefinition.id, type: this.type, values: this.toValueJson()}
	}

	toJson(): WorkspaceViewFilterApiDto {
		return {
			id: this.id,
			columnDefinitionId: this.columnDefinition.id,
			type: this.type,
			values: this.toValueJson()
		}
	}

	toQueryJson(): QuerySearchFilterApiDto {
		return {
			propertyName: this.columnDefinition.searchPath,
			operatorType: this.type,
			values: this.toValueJson(),
			columnType: this.columnDefinition.type
		}
	}

	toValueJson(): FilterValueApiDto[] {
		switch (this.columnDefinition.type) {
			case ColumnTypeEnum.LeadTag:
			case ColumnTypeEnum.JobTag:
			case ColumnTypeEnum.LeadStatus:
			case ColumnTypeEnum.JobStatus:
			case ColumnTypeEnum.BillStatus:
			case ColumnTypeEnum.Contact:
			case ColumnTypeEnum.JobType:
			case ColumnTypeEnum.Brand:
			case ColumnTypeEnum.PartnerBrand:
			case ColumnTypeEnum.Creator:
			case ColumnTypeEnum.User:
			case ColumnTypeEnum.LeadAssignee:
			case ColumnTypeEnum.JobAssignee:
			case ColumnTypeEnum.PaymentMethod:
			case ColumnTypeEnum.Merchant:
			case ColumnTypeEnum.Reconciliation:
			case ColumnTypeEnum.InvoicePath:
			case ColumnTypeEnum.BankAccountType:
			case ColumnTypeEnum.JobWarrantyRegistrationStatus:
				return (this.values as Option<string[]>).getOrElse([]);
			case ColumnTypeEnum.LeadCustomField:
			case ColumnTypeEnum.JobCustomField: {
				let values: (string | FilterValueApiDto | FilterValueApiDto[] | DateFilterModel)[] = [];
				(this.values as Option<(string | DateFilterModel)[]>).getOrElse([]).forEach((v) => {
					if (v instanceof DateFilterModel) {
						if (this.type === DateFilterType.Between) {
							values.push((v as DateFilterModel).toQueryJson(DateUtils.ISO_DATE_FORMAT));
						}
						else {
							values.push((v as DateFilterModel).toQueryJson());
						}
					}
					else {
						values.push(v);
					}
				});
				return (_.flatten(values)).filter(el => el !== null) as (string | FilterValueApiDto)[];
			}
			case ColumnTypeEnum.Appointments:
			case ColumnTypeEnum.Date: {
				return this.values.map(v => (v as DateFilterModel).toQueryJson()).orNull
			}
			case ColumnTypeEnum.Currency:
			case ColumnTypeEnum.Long:
			case ColumnTypeEnum.Percentage:
			case ColumnTypeEnum.Decimal: {
				return this.values.map(v => (v as NumberFilterModel).toQueryJson()).orNull
			}
			default:
				return this.values.map(v => [`${v}`]).orNull;
		}
	}

	isNegated() {
		return this.type === FilterTypeEnum.NotIn || this.type === FilterTypeEnum.NotEqual
	}
}

export type FilterValueType =
	string
	| string[]
	| boolean
	| FilterModelType
	| NumberFilterModelType
	| (string | FilterValueApiDto | DateFilterModel)[];

export enum FilterTypeEnum {
	Equal = "EQUAL",
	NotEqual = "NOT_EQUAL",
	In = "IN",
	NotIn = "NOT_IN"
}
